import { computed, ComputedRef, reactive, Ref, ref } from 'vue';
import {
    AddBasketItemRequest,
    AddClickAndCollectBasketItemRequest,
    AddGiftPackagingRequest,
    AddOutletItemRequest,
    AddRedeemCodeRequest,
    B2CClientService,
    BaseProductItem,
    BasketDeliverable,
    BasketModel,
    BasketModelSummary,
    ChangeToClickAndCollectRequest,
    ChangeToPickAndRequest,
    CheckoutDeliverableType,
    Deliverable,
    ReplaceItemRequest,
    GetBasketMode,
    AddOrUpdateModuleConfigurationRequest,
} from '@/b2c/shared/api/server-apis';
import Deferred from '@/core/async/Deferred';
import utilities from '@/shared/utilities.service';
import basketTrackingService from '@/shared/tracking/basket-tracking.service';

export interface IBasketState {
    pending: boolean;
    basket: BasketModel;
}

const state: IBasketState = reactive({
    pending: false,
    basket: {} as BasketModel,
});

const basketId: Ref<string> = ref('');
const initDeferred = new Deferred<BasketModel>();
const initialized: Promise<BasketModel> = initDeferred.promise;

export default function useBasket() {
    const pending: ComputedRef<boolean> = computed(() => state.pending);
    const basket: ComputedRef<BasketModel> = computed(() => state.basket);
    const deliverables: ComputedRef<BasketDeliverable[]> = computed(() => state.basket?.deliverables ?? [] as Deliverable[]);
    const summary: ComputedRef<BasketModelSummary> = computed(() => state.basket?.summary ?? {} as BasketModelSummary);
    const quantity: ComputedRef<number> = computed(() => state.basket?.numberOfItems ?? 0);
    const upSellItemCounter: ComputedRef<number> = computed(() => state.basket?.upSellItemCounter ?? 0);
    const isEmpty: ComputedRef<boolean> = computed(() => state.basket?.numberOfItems < 1 ?? false);
    const isLoading: ComputedRef<boolean> = computed(() => quantity.value < 1 && !isEmpty.value);
    const isCheckoutable: ComputedRef<boolean> = computed(() => state.basket.isCheckoutAble);
    const clickAndCollectStoreId = computed(() => state.basket.clickAndCollectStoreId);
    const hasClickAndCollectDeliverable = computed(() => deliverables.value.some((deliverable) => deliverable.type === CheckoutDeliverableType.ClickAndCollect));
    const hasSufficientPayments = computed(() => state.basket.hasSufficientPayments);
    const buyXAndGetYPctDiscount = computed(() => summary?.value.buyAndGetYPctDiscount);

    const lineItemTotalCount:ComputedRef<number> = computed(() => {
        let count = 0;
        deliverables.value.forEach(deliverable => count += deliverable.items.length);
        return count;
    });

    const init = async(id: string): Promise<BasketModel> => {
        basketId.value = id;
        try {
            const basket = await get();
            initDeferred.resolve(basket);
            return basket;
        } catch (e) {
            return Promise.reject();
        }
    };

    const get = async(getBasketMode: GetBasketMode = GetBasketMode.Default): Promise<BasketModel> => {
        return B2CClientService.getBasket(basketId.value, getBasketMode)
            .then(utilities.unwrapModel)
            .then(setBasket);
    };

    const replaceItem = async(request: ReplaceItemRequest, messageId?: string): Promise<BasketModel> => {
        return B2CClientService.replaceItem(basketId.value, request, messageId)
            .then(utilities.unwrapModel)
            .then(setBasket);
    };

    const addRegular = async(request: AddBasketItemRequest, messageId?: string): Promise<BasketModel> => {
        return B2CClientService.addRegularItem(basketId.value, request, messageId)
            .then(utilities.unwrapModel)
            .then(setBasket);
    };

    const addPickAndPack = async(request: AddBasketItemRequest, messageId?: string): Promise<BasketModel> => {
        return B2CClientService.addPickAndPackItem(basketId.value, request, messageId)
            .then(utilities.unwrapModel)
            .then(setBasket);
    };

    const addClickAndCollect = async(request: AddClickAndCollectBasketItemRequest, messageId?: string): Promise<BasketModel> => {
        return B2CClientService.addClickAndCollectItem(basketId.value, request, messageId)
            .then(utilities.unwrapModel)
            .then(setBasket);
    };

    const deleteBasket = async(messageId?: string): Promise<BasketModel> => {
        return B2CClientService.deleteBasket(basketId.value, messageId)
            .then(utilities.unwrapModel)
            .then(setBasket);
    };

    const addRedeemCode = async(request: AddRedeemCodeRequest, messageId?: string): Promise<BasketModel> => {
        return B2CClientService.redeemCode(basketId.value, request, messageId)
            .then(utilities.unwrapModel)
            .then(setBasket);
    };

    const removePromoCode = async(code: string, messageId?: string): Promise<BasketModel> => {
        return B2CClientService.removePromoCode(basketId.value, code, messageId)
            .then(utilities.unwrapModel)
            .then(setBasket);
    };

    const removeGiftCard = async(code: string, messageId?: string): Promise<BasketModel> => {
        return B2CClientService.removeGiftCardPaymentFromBasket(basketId.value, code, messageId)
            .then(utilities.unwrapModel)
            .then(setBasket);
    };

    const addCustomerOfferItemToBasket = async(customerOfferId: string | null, customerOfferLineItemIds: string[], messageId?: string): Promise<BasketModel> => {
        return B2CClientService.addCustomerOfferItemToBasket(basketId.value, customerOfferId, customerOfferLineItemIds, messageId)
            .then(utilities.unwrapModel)
            .then(setBasket);
    };

    const updateItemQuantity = async(itemId: number, request: { quantity: number, delta: boolean }, messageId?: string): Promise<BasketModel> => {
        if (state.pending) {
            return Promise.resolve(state.basket);
        }

        const isDecrease = state.basket.deliverables.some(deliverable => deliverable.items.some((item) => item.id === itemId && (item as BaseProductItem).quantity > request.quantity));

        state.pending = true;
        await B2CClientService.updateItemQuantity(basketId.value, itemId, request, messageId)
            .then(utilities.unwrapModel)
            .then(setBasket)
            .finally(() => state.pending = false);

        if (isDecrease) {
            basketTrackingService.removeFromBasket(state.basket, itemId);
        } else {
            basketTrackingService.basketQuantity(state.basket, itemId);
        }
        return state.basket;
    };

    const deleteItem = async(itemId: number, messageId?: string): Promise<BasketModel> => {
        if (state.pending) {
            return Promise.resolve(state.basket);
        }
        basketTrackingService.removeFromBasket(basket.value, itemId);
        state.pending = true;
        await B2CClientService.deleteItem(basketId.value, itemId, messageId)
            .then(utilities.unwrapModel)
            .then(setBasket)
            .finally(() => state.pending = false);
        return state.basket;
    };

    const changeToClickAndCollect = async(request: ChangeToClickAndCollectRequest, messageId ?: string): Promise<BasketModel> => {
        if (state.pending) {
            return Promise.resolve(state.basket);
        }
        state.pending = true;
        await B2CClientService.changeToClickAndCollect(basketId.value, request, messageId)
            .then(utilities.unwrapModel)
            .then(setBasket)
            .finally(() => state.pending = false);
        return state.basket;
    };

    const changeClickAndCollectStore = async(storeId: string, messageId ?: string): Promise<BasketModel> => {
        if (state.pending) {
            return Promise.resolve(state.basket);
        }
        state.pending = true;
        await B2CClientService.changeClickAndCollectStore(basketId.value, storeId, messageId)
            .then(utilities.unwrapModel)
            .then(setBasket)
            .finally(() => state.pending = false);
        return state.basket;
    };

    const changeToPickAndPack = async(request: ChangeToPickAndRequest, messageId ?: string): Promise<BasketModel> => {
        if (state.pending) {
            return Promise.resolve(state.basket);
        }
        state.pending = true;
        await B2CClientService.changeToPickAndPack(basketId.value, request, messageId)
            .then(utilities.unwrapModel)
            .then(setBasket)
            .finally(() => state.pending = false);
        return state.basket;
    };

    const addGiftPackingToItem = async(itemId: number, request: AddGiftPackagingRequest, messageId ?: string): Promise<BasketModel> => {
        if (state.pending) {
            return Promise.resolve(state.basket);
        }
        state.pending = true;
        await B2CClientService.addGiftPackingToItem(basketId.value, itemId, request, messageId)
            .then(utilities.unwrapModel)
            .then(setBasket)
            .finally(() => state.pending = false);
        return state.basket;
    };

    const removeGiftPackingFromItem = async(itemId: number, messageId ?: string): Promise<BasketModel> => {
        if (state.pending) {
            return Promise.resolve(state.basket);
        }
        state.pending = true;
        await B2CClientService.removeGiftPackingFromItem(basketId.value, itemId, messageId)
            .then(utilities.unwrapModel)
            .then(setBasket)
            .finally(() => state.pending = false);
        return state.basket;
    };

    const addOutletItem = async(request: AddOutletItemRequest, messageId?: string): Promise<BasketModel> => {
        return B2CClientService.addOutletItem(basketId.value, request, messageId)
            .then(utilities.unwrapModel)
            .then(setBasket);
    };

    const addOrUpdateModuleConfiguration = async(configurationId: string, request: AddOrUpdateModuleConfigurationRequest, messageId?: string): Promise<BasketModel> => {
        return B2CClientService.addOrUpdateModuleConfiguration(basketId.value, configurationId, request, messageId)
            .then(utilities.unwrapModel)
            .then(setBasket);
    };

    const hasMyBoliaBoostCampaignDiscount = computed(() => state.basket.hasMyBoliaBoostCampaignDiscount);

    return {
        initialized,
        basket,
        basketId,
        quantity,
        deliverables,
        summary,
        isEmpty,
        isLoading,
        isCheckoutable,
        hasMyBoliaBoostCampaignDiscount,
        pending,
        lineItemTotalCount,
        upSellItemCounter,
        clickAndCollectStoreId,
        hasSufficientPayments,
        hasClickAndCollectDeliverable,
        buyXAndGetYPctDiscount,
        deleteBasket,
        updateItemQuantity,
        deleteItem,
        addRegular,
        addPickAndPack,
        addClickAndCollect,
        addRedeemCode,
        removePromoCode,
        removeGiftCard,
        get,
        replaceItem,
        init,
        changeToClickAndCollect,
        changeClickAndCollectStore,
        changeToPickAndPack,
        addGiftPackingToItem,
        removeGiftPackingFromItem,
        addOutletItem,
        addCustomerOfferItemToBasket,
        addOrUpdateModuleConfiguration,
    };
}

function setBasket(basket: BasketModel | undefined): BasketModel {
    state.basket = basket ?? {} as BasketModel;
    return state.basket;
}
